import type { HubFeatureFlagsResponseDto } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const INTERNAL_FEATURE_FLAGS_QUERY_KEY = [QueryKeys.INTERNAL_FEATURE_FLAGS];

/**
 * Fetches all internal feature flags from the backend.
 * These flags are managed via internal-cms and cached with staleTime: Infinity.
 * The cache is invalidated via SSE when flags change.
 */
export const useInternalFeatureFlags = () => {
  const apiHelper = useApi();

  return useQuery({
    queryKey: INTERNAL_FEATURE_FLAGS_QUERY_KEY,
    queryFn: async (): Promise<HubFeatureFlagsResponseDto> => {
      return apiHelper.getOrThrow(API_ENDPOINT.HUB_FEATURE_FLAGS_V2);
    },
    staleTime: Infinity, // Never refetch automatically, rely on SSE invalidation
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Hook to invalidate internal feature flags cache.
 * Used by useNotifications when receiving FEATURE_FLAG_CHANGED events.
 */
export const useInvalidateInternalFeatureFlags = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: INTERNAL_FEATURE_FLAGS_QUERY_KEY });
  };
};
